import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../hooks/useAuth';
import dayjs from 'dayjs';
import { useState } from 'react';

const Dashboard = () => {
  const { t } = useTranslation();
  const [selectedCompany, setSelectedCompany] = useState('');

  // Fetch companies for filter
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies');
      return response.data; // API returns companies directly, not wrapped in .companies
    },
  });

  // Fetch dashboard statistics
  const { data: statsData, isLoading } = useQuery({
    queryKey: ['dashboard-stats', selectedCompany],
    queryFn: async () => {
      const params = selectedCompany ? `?companyId=${selectedCompany}` : '';
      const response = await api.get(`/invoices/stats${params}`);
      return response.data;
    },
  });

  // Extract data from API response
  const stats = statsData?.stats || {
    total: 0,
    paid: 0,
    unpaid: 0,
    void: 0,
    draft: 0,
    sent: 0,
  };

  const revenue = statsData?.revenue || {
    total: 0,
    paid: 0,
    unpaid: 0,
    outstanding: 0,
  };

  const monthlyBreakdown = statsData?.monthlyBreakdown || [];
  const recentInvoices = statsData?.recentInvoices || [];
  const pendingInvoices = statsData?.pendingInvoices || [];

  // Calculate employee count based on selected company
  const getEmployeeCount = () => {
    if (selectedCompany) {
      // Show employees for selected company only
      const company = companies?.find(c => c._id === selectedCompany);
      return company?.employeeCount || 0;
    } else {
      // Show total employees across all companies
      return companies?.reduce((sum, company) => sum + (company.employeeCount || 0), 0) || 0;
    }
  };

  const totalEmployees = getEmployeeCount();

  // Process breakdown data - filter out zero values
  const monthlyData = monthlyBreakdown.filter(item => item.total > 0 || item.paid > 0 || item.unpaid > 0);
  
  // Group by year for yearly breakdown
  const yearlyData = monthlyBreakdown.reduce((acc, item) => {
    const year = item.year || new Date(item.month).getFullYear();
    if (!acc[year]) {
      acc[year] = {
        year,
        total: 0, // Count of invoices
        paid: 0, // Count of paid invoices
        unpaid: 0, // Count of unpaid invoices
        revenue: 0, // Total revenue
        paidRevenue: 0, // Paid amount
        unpaidRevenue: 0 // Unpaid amount
      };
    }
    acc[year].total += item.total;
    acc[year].paid += item.paid;
    acc[year].unpaid += item.unpaid;
    acc[year].revenue += item.revenue;
    acc[year].paidRevenue += item.paidRevenue;
    acc[year].unpaidRevenue += item.unpaidRevenue;
    return acc;
  }, {});

  const yearlyBreakdown = Object.values(yearlyData).filter(item => item.total > 0 || item.paid > 0 || item.unpaid > 0);

  // Debug log
  console.log('Dashboard Stats Data:', statsData);
  console.log('Stats:', stats);
  console.log('Revenue:', revenue);
  console.log('Monthly Data:', monthlyData);
  console.log('Yearly Data:', yearlyBreakdown);

  const formatCurrency = (amount, currency = 'EUR') => {
    if (!amount || isNaN(amount) || amount === 0) {
      return '€0.00';
    }
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      unpaid: 'bg-orange-100 text-orange-800',
      void: 'bg-red-100 text-red-800',
    };
    return colors[status] || colors.draft;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('dashboard.welcomeMessage')}
          </p>
        </div>
        
        {/* Company Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">{t('dashboard.filterByCompany')}:</label>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-[200px]"
          >
            <option value="">{t('dashboard.allCompanies')}</option>
            {companies?.map((company) => (
              <option key={company._id} value={company._id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-medium">👥</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                {selectedCompany ? t('dashboard.companyEmployees') : t('dashboard.totalEmployees')}
              </p>
              <p className="text-2xl font-semibold text-gray-900">{totalEmployees}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-medium">✅</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('dashboard.paidInvoices')}</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.paid}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-medium">💸</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('dashboard.pendingInvoices')}</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.unpaid}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-medium">💰</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('dashboard.totalRevenue')}</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(revenue.total)}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-medium">💵</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('dashboard.paidAmount')}</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(revenue.paid)}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-medium">⚠️</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('dashboard.outstanding')}</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(revenue.outstanding)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="card">
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900">{t('dashboard.statusBreakdown')}</h2>
          <p className="text-sm text-gray-500">{t('dashboard.statusDescription')}</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
            <div className="text-sm text-green-700">{t('invoices.statuses.paid')}</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{stats.unpaid}</div>
            <div className="text-sm text-orange-700">{t('invoices.statuses.unpaid')}</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats.sent}</div>
            <div className="text-sm text-blue-700">{t('invoices.statuses.sent')}</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-600">{stats.draft}</div>
            <div className="text-sm text-gray-700">{t('invoices.statuses.draft')}</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{stats.void}</div>
            <div className="text-sm text-red-700">{t('invoices.statuses.void')}</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{stats.total}</div>
            <div className="text-sm text-purple-700">{t('common.total')}</div>
          </div>
        </div>
      </div>

      {/* Recent Invoices - Split into 2 sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-900">{t('dashboard.recentInvoices')}</h2>
            <Link
              to="/invoices"
              className="btn btn-primary btn-sm"
            >
              {t('dashboard.viewAll')}
            </Link>
          </div>

        {recentInvoices && recentInvoices.length > 0 ? (
          <div className="overflow-hidden">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('invoices.invoiceNumber')}</th>
                  <th>{t('invoices.customer')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('common.status')}</th>
                  <th className="text-right">{t('common.total')}</th>
                  <th className="text-right">{t('invoices.paidAmount')}</th>
                </tr>
              </thead>
              <tbody>
                {recentInvoices.map((invoice) => (
                  <tr key={invoice._id}>
                    <td>
                      <Link
                        to={`/invoices/${invoice._id}`}
                        className="font-medium text-primary-600 hover:text-primary-500"
                      >
                        {invoice.invoiceNumber}
                      </Link>
                    </td>
                    <td>{invoice.customerName}</td>
                    <td>{dayjs(invoice.invoiceDate).format('DD/MM/YYYY')}</td>
                    <td>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                        {t(`invoices.statuses.${invoice.status}`)}
                      </span>
                    </td>
                    <td className="text-right font-medium">
                      {formatCurrency(invoice.total)}
                    </td>
                    <td className="text-right font-medium">
                      {formatCurrency(invoice.paidAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">📄</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('dashboard.noInvoices')}</h3>
            <p className="text-gray-500 mb-4">{t('dashboard.createFirst')}</p>
            <Link to="/invoices/new" className="btn btn-primary">
              {t('invoices.createInvoice')}
            </Link>
          </div>
        )}
        </div>

        {/* Pending Invoices */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-900">{t('dashboard.pendingInvoices')}</h2>
            <Link
              to="/invoices"
              className="btn btn-primary btn-sm"
            >
              {t('dashboard.viewAll')}
            </Link>
          </div>

          {pendingInvoices && pendingInvoices.length > 0 ? (
            <div className="overflow-hidden">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('invoices.invoiceNumber')}</th>
                    <th>{t('invoices.customer')}</th>
                    <th>{t('common.date')}</th>
                    <th>{t('common.status')}</th>
                    <th className="text-right">{t('common.total')}</th>
                    <th className="text-right">{t('invoices.paidAmount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvoices.map((invoice) => (
                    <tr key={invoice._id}>
                      <td>
                        <Link
                          to={`/invoices/${invoice._id}`}
                          className="font-medium text-primary-600 hover:text-primary-500"
                        >
                          {invoice.invoiceNumber}
                        </Link>
                      </td>
                      <td>{invoice.customerName}</td>
                      <td>{dayjs(invoice.invoiceDate).format('DD/MM/YYYY')}</td>
                      <td>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                          {t(`invoices.statuses.${invoice.status}`)}
                        </span>
                      </td>
                      <td className="text-right font-medium">
                        {formatCurrency(invoice.total)}
                      </td>
                      <td className="text-right font-medium">
                        {formatCurrency(invoice.paidAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-6xl mb-4">📄</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('dashboard.noPending')}</h3>
              <p className="text-gray-500 mb-4">{t('dashboard.allUpToDate')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Breakdown Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Breakdown */}
        {monthlyData.length > 0 && (
          <div className="card">
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900">{t('dashboard.monthlyBreakdown')}</h2>
              <p className="text-sm text-gray-500">{t('dashboard.monthlyDescription')}</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('dashboard.month')}</th>
                    <th className="text-right">{t('dashboard.invoices')}</th>
                    <th className="text-right">{t('dashboard.revenue')}</th>
                    <th className="text-right">{t('dashboard.paid')}</th>
                    <th className="text-right">{t('dashboard.unpaid')}</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.map((month, index) => (
                    <tr key={month.month || `month-${index}`}>
                      <td className="font-medium">{dayjs(month.month).format('MMM YYYY')}</td>
                      <td className="text-right">{month.total}</td>
                      <td className="text-right font-medium">{formatCurrency(month.revenue)}</td>
                      <td className="text-right">{formatCurrency(month.paidRevenue)}</td>
                      <td className="text-right">{formatCurrency(month.unpaidRevenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Yearly Breakdown */}
        {yearlyBreakdown.length > 0 && (
          <div className="card">
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900">{t('dashboard.yearlyBreakdown')}</h2>
              <p className="text-sm text-gray-500">{t('dashboard.yearlyDescription')}</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('dashboard.year')}</th>
                    <th className="text-right">{t('dashboard.invoices')}</th>
                    <th className="text-right">{t('dashboard.revenue')}</th>
                    <th className="text-right">{t('dashboard.paid')}</th>
                    <th className="text-right">{t('dashboard.unpaid')}</th>
                  </tr>
                </thead>
                <tbody>
                  {yearlyBreakdown.map((year, index) => (
                    <tr key={year.year || `year-${index}`}>
                      <td className="font-medium">{year.year}</td>
                      <td className="text-right">{year.total}</td>
                      <td className="text-right font-medium">{formatCurrency(year.revenue)}</td>
                      <td className="text-right">{formatCurrency(year.paidRevenue)}</td>
                      <td className="text-right">{formatCurrency(year.unpaidRevenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
